import React, { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import {
    Button,
    Col,
    Container,
    Nav,
    NavItem,
    NavLink,
    Row,
    TabContent,
    TabPane,
} from "reactstrap"
import classnames from "classnames"
import MerchantGroupForm from "./merchant-group-form"
import MerchantGroupBankingForm from "./merchant-group-banking-form"
import MerchantGroupCommunicationForm from "./communication-form"
import { PageTabs, PageTitle } from "constants/ui"
import MerchantGroupContactForm from "./contact-form"
import MerchantGroupAdminUserForm from "./admin-user-form"
import CButton from "components/Common/CButton"
import { getUserAuthorized } from "helpers/backend_helper"
import AccessControlWrapper from "components/Common/PermissionButton"
import PermissionTabs from "components/Common/PermissionTabs"
import { getTabNames, getTabPermissions, isTabMandatory } from "helpers/tab_helper"

const MerchantGroupDetail = props => {
    const { match } = props
    const page_title = match?.params?.id
        ? PageTitle["MERCHANT_GROUP_DETAIL"]
        : PageTitle["MERCHANT_GROUP_ADD"]

    const page_type = match?.params?.id ? "detail" : "add"

    const parent_page_title = PageTitle.MERCHANT_GROUP_LIST
    document.title = page_title

    const [activeTab, setactiveTab] = useState("1")
    // Only add tab flow state for create mode
    const [completedTabs, setCompletedTabs] = useState(
        page_type === "detail" ? ["1", "2", "3", "4"] : []
    )
    const [enabledTabs, setEnabledTabs] = useState(
        page_type === "detail" ? ["1", "2", "3", "4"] : ["1"]
    ) // Track which tabs are enabled
    const [merchantGroupCreated, setMerchantGroupCreated] = useState(
        page_type === "detail"
    )
    const [merchantGroupFormRef, setMerchantGroupFormRef] = useState(null)
    const [merchantGroupFormReadOnly, setMerchantGroupFormReadOnly] =
        useState(false)

    const [createdMerchantGroupID, setCreatedMerchantGroupID] = useState(null)

    // Permission checking state
    const [tabPermissions, setTabPermissions] = useState({})
    const [isCheckingPermission, setIsCheckingPermission] = useState(false)
    const [permissionErrors, setPermissionErrors] = useState({})

    // Banking form finish tracking
    const [finishRequested, setFinishRequested] = useState(false)
    const [previousBankingState, setPreviousBankingState] = useState(null)

    // State to track legal entity info for breadcrumb
    const [legalEntityInfo, setLegalEntityInfo] = useState({
        legalEntityName: "",
        legalEntityAddress: ""
    })


    // Get communications, contacts, banking, admin user data, and merchantGroupDetail from Redux
    const {
        merchantGroupCommunications,
        merchantGroupContacts,
        merchantGroupBanking,
        bankingError,
        merchantGroupAdminUser,
        userMenu,
        merchantGroupDetail
    } = useSelector(state => ({
        merchantGroupCommunications:
            state.merchantGroup.merchantGroupCommunications || [],
        merchantGroupContacts: state.merchantGroup.merchantGroupContacts || [],
        merchantGroupBanking: state.merchantGroup.merchantGroupBankingList || [],
        bankingError: state.merchantGroup.bankingError,
        merchantGroupAdminUser: state.merchantGroup.merchantGroupAdminUser,
        userMenu: state.userMenu?.userMenu || [],
        merchantGroupDetail: state.merchantGroup.merchantGroupDetail
    }))

    const pageKey = "MERCHANT_GROUP_DETAIL"
    const tabNames = getTabNames(pageKey, userMenu)
    const tabPermissionsArray = getTabPermissions(pageKey, page_type, userMenu)

    // Generic form state checker
    const hasFormContent = (tabName) => {
        switch (tabName.toLowerCase()) {
            case 'merchant group':
                return merchantGroupCreated
            case 'communications':
                return merchantGroupCommunications.length > 0
            case 'contacts':
                return merchantGroupContacts.length > 0
            case 'bankings':
                return merchantGroupBanking.length > 0
            case 'admin user':
                return merchantGroupAdminUser && Object.keys(merchantGroupAdminUser).length > 0
            default:
                return false
        }
    }

    // Permission checking function with API failure handling
    const checkTabPermission = async (tabId, forceRefresh = false) => {
        // Skip permission check for detail mode
        if (page_type === "detail") {
            return true
        }

        // Use cached permission if available and not forcing refresh
        if (!forceRefresh && tabPermissions[tabId] !== undefined) {
            return tabPermissions[tabId]
        }

        const permission = tabPermissionsArray[tabId - 1]
        if (!permission) {
            return true
        }

        try {
            setIsCheckingPermission(true)
            setPermissionErrors(prev => ({ ...prev, [tabId]: null }))

            const result = await getUserAuthorized({
                headers: {
                    "input-parameters": {
                        requestURI: permission.contextUri,
                        requestMethod: permission.actionType,
                    },
                },
            })

            // Check if permission is granted based on API response
            const hasPermission =
                result?.type === "USER_AUTHORIZATION_STATUS" &&
                result?.desc === "true" &&
                result?.code === "200"

            setTabPermissions(prev => ({ ...prev, [tabId]: hasPermission }))
            return hasPermission
        } catch (error) {
            console.error(`Permission check failed for tab ${tabId}:`, error)

            // Handle API failure - default to deny for security but mark as error
            setPermissionErrors(prev => ({
                ...prev,
                [tabId]: {
                    message: "Unable to verify permissions. Please try again.",
                    isApiError: true,
                },
            }))
            setTabPermissions(prev => ({ ...prev, [tabId]: false }))
            return false
        } finally {
            setIsCheckingPermission(false)
        }
    }

    // Check permission for merchant group tab on page load
    useEffect(() => {
        if (page_type === "add") {
            checkTabPermission(1)
        }
    }, [page_type])

    // Update legal entity info from Redux in detail mode
    useEffect(() => {
        if (page_type === "detail" && merchantGroupDetail) {
            const merchantGroup = Array.isArray(merchantGroupDetail)
                ? merchantGroupDetail.find(mg => mg.merchantGroupID === match?.params?.id)
                : merchantGroupDetail

            if (merchantGroup) {
                setLegalEntityInfo({
                    legalEntityName: merchantGroup.legalEntityName || "",
                    legalEntityAddress: merchantGroup.legalEntityAddress || ""
                })
            }
        }
    }, [page_type, merchantGroupDetail, match?.params?.id])

    // Monitor banking form success when finish is requested
    useEffect(() => {
        if (finishRequested && merchantGroupBanking && !bankingError) {
            // Banking form was successfully submitted after finish was requested
            if (merchantGroupBanking !== previousBankingState) {
                setFinishRequested(false)
                setPreviousBankingState(merchantGroupBanking)

                // Navigate to merchant group detail page
                if (createdMerchantGroupID) {
                    props.history.push(`/merchant-groups/${createdMerchantGroupID}`)
                } else {
                    props.history.push("/merchant-groups")
                }
            }
        } else if (finishRequested && bankingError) {
            // Banking form submission failed - reset flag but don't navigate
            setFinishRequested(false)
        }
    }, [
        finishRequested,
        merchantGroupBanking,
        bankingError,
        previousBankingState,
        createdMerchantGroupID,
        props.history,
    ])

    const toggle = tab => {
        // For detail mode, keep original behavior
        if (page_type === "detail") {
            if (activeTab !== tab) {
                setactiveTab(tab)
            }
            return
        }

        // For create mode, only allow navigation to enabled tabs
        if (enabledTabs.includes(tab)) {
            setactiveTab(tab)
        }
    }

    const handleMerchantGroupCreation = merchantGroupID => {
        setMerchantGroupCreated(true)
        setMerchantGroupFormReadOnly(true)
        setCreatedMerchantGroupID(merchantGroupID)
        setCompletedTabs(prev => (prev.includes("1") ? prev : [...prev, "1"]))
        // Enable the next tab (Communication) after merchant group creation
        setEnabledTabs(prev => {
            if (!prev.includes("2")) {
                return [...prev, "2"]
            }
            return prev
        })
    }

    const handleTabCompletion = tabId => {
        if (!completedTabs.includes(tabId)) {
            setCompletedTabs(prev => [...prev, tabId])
        }
    }

    // Handler for form changes (called from merchant-group-form)
    const handleFormChange = (formValues) => {
        if (formValues) {
            setLegalEntityInfo({
                legalEntityName: formValues.legalEntityName || "",
                legalEntityAddress: formValues.legalEntityAddress || ""
            })
        }
    }

    const navigateToNextTab = async () => {
        const currentTabIndex = parseInt(activeTab)
        const nextTab = String(currentTabIndex + 1)
        if (currentTabIndex < tabNames.length) {
            // Enable the next tab when navigating
            setEnabledTabs(prev => {
                if (!prev.includes(nextTab)) {
                    return [...prev, nextTab]
                }
                return prev
            })

            // Check permission for next tab on forward navigation (force refresh)
            if (page_type === "add" && tabPermissionsArray[parseInt(nextTab) - 1]) {
                await checkTabPermission(parseInt(nextTab), true)
            }

            setactiveTab(nextTab)
        }
    }

    const navigateToPreviousTab = () => {
        const currentTabIndex = parseInt(activeTab)
        const previousTab = String(currentTabIndex - 1)
        if (currentTabIndex > 1) {
            setactiveTab(previousTab)
        }
    }

    const isTabDisabled = tabIndex => {
        if (page_type === "detail") return false // All tabs enabled for detail/edit mode
        return !enabledTabs.includes(String(tabIndex)) // Tab is disabled if not in enabledTabs
    }

    const renderTabContent = (tabName, tabIndex) => {
        switch (tabName.toLowerCase()) {
            case 'merchant group':
                return (
                    <MerchantGroupForm
                        page={page_type}
                        {...props}
                        onMerchantGroupCreated={handleMerchantGroupCreation}
                        onTabCompletion={handleTabCompletion}
                        navigateToNextTab={navigateToNextTab}
                        activeTab={activeTab}
                        onFormRef={setMerchantGroupFormRef}
                        onReadOnlyChange={setMerchantGroupFormReadOnly}
                        onFormChange={handleFormChange}
                    />
                )

            case 'communications':
                return (
                    <MerchantGroupCommunicationForm
                        page={page_type}
                        {...props}
                        onTabCompletion={handleTabCompletion}
                        activeTab={activeTab}
                        tabId={String(tabIndex)}
                        createdMerchantGroupID={createdMerchantGroupID}
                    />
                )

            case 'contacts':
                return (
                    <MerchantGroupContactForm
                        page={page_type}
                        {...props}
                        onTabCompletion={handleTabCompletion}
                        activeTab={activeTab}
                        tabId={String(tabIndex)}
                        createdMerchantGroupID={createdMerchantGroupID}
                    />
                )

            case 'bankings':
                return (
                    <MerchantGroupBankingForm
                        page={page_type}
                        {...props}
                        onTabCompletion={handleTabCompletion}
                        activeTab={activeTab}
                        tabId={String(tabIndex)}
                        createdEntityID={createdMerchantGroupID}
                    />
                )

            case 'admin user':
                return (
                    <MerchantGroupAdminUserForm
                        page={page_type}
                        {...props}
                        onTabCompletion={handleTabCompletion}
                        activeTab={activeTab}
                        tabId={String(tabIndex)}
                        createdMerchantGroupID={createdMerchantGroupID}
                    />
                )

            default:
                return (
                    <div className="text-center p-5">
                        <h5>Tab content not implemented</h5>
                        <p className="text-muted">Content for "{tabName}" tab is not yet implemented.</p>
                    </div>
                )
        }
    }

    const handleFinish = () => {
        if (createdMerchantGroupID) {
            props.history.push(`/merchant-groups/${createdMerchantGroupID}`)
        } else {
            props.history.push("/merchant-groups")
        }
    }

    const handleSkipFinish = () => {
        props.history.push("/merchant-groups")
    }

    const renderNavigationButtons = (tabName, tabIndex) => {
        const isLastTab = tabIndex >= tabNames.length
        const isMandatory = isTabMandatory(pageKey, tabName, userMenu)
        const hasContent = hasFormContent(tabName)
        
        // Last tab - special finish logic with mandatory check
        if (isLastTab) {
            if (isMandatory) {
                return hasContent ? 
                    <CButton.Primary text="Finish" onClick={handleFinish} disabled={isCheckingPermission} /> : 
                    null
            } else {
                return hasContent ? 
                    <CButton.Primary text="Finish" onClick={handleFinish} disabled={isCheckingPermission} /> : 
                    <CButton.Secondary text="Skip & Finish" onClick={handleSkipFinish} disabled={isCheckingPermission} />
            }
        }
        
        // All other tabs (first, middle) - unified logic
        if (isMandatory) {
            return hasContent ? 
                <CButton.Primary text="Continue" onClick={navigateToNextTab} disabled={isCheckingPermission || isLastTab} /> : 
                null
        } else {
            return hasContent ? 
                <CButton.Primary text="Continue" onClick={navigateToNextTab} disabled={isCheckingPermission || isLastTab} /> : 
                <CButton.Secondary text="Skip" onClick={navigateToNextTab} disabled={isCheckingPermission || isLastTab} />
        }
    }

    // No Access Message Component
    const NoAccessMessage = ({ tabId, onRetry }) => {
        const permissionError = permissionErrors[tabId]
        const isApiError = permissionError?.isApiError

        return (
            <div className="text-center p-5">
                <i
                    className={`display-4 mb-3 ${isApiError
                        ? "mdi mdi-wifi-off text-warning"
                        : "mdi mdi-lock-outline text-muted"
                        }`}
                ></i>
                <h5>{isApiError ? "Connection Error" : "Access Restricted"}</h5>
                <p className="text-muted">
                    {isApiError
                        ? "Unable to verify permissions. Please try again."
                        : "You do not have access to this module. Contact your administrator for access rights."}
                </p>
            </div>
        )
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid={true}>
                    <Breadcrumbs
                        titleLink="/merchant-groups"
                        title={parent_page_title}
                        breadcrumbItem={page_title}
                        secondaryInfo={[
                            { label: "Legal Entity Name", value: legalEntityInfo.legalEntityName },
                            { label: "Legal Entity Address", value: legalEntityInfo.legalEntityAddress }
                        ]}
                    />
                    <Row>
                        <Col md="12">
                            <PermissionTabs
                                tabs={tabNames}
                                activeTab={activeTab}
                                onTabChange={toggle}
                                isTabDisabled={isTabDisabled}
                                tabPermissions={tabPermissionsArray}
                                history={props.history}
                            />

                            <TabContent activeTab={activeTab} className="p-3 text-muted">
                                {tabNames.map((tabName, index) => {
                                    const tabIndex = index + 1
                                    const tabId = String(tabIndex)

                                    return (
                                        <TabPane key={tabId} tabId={tabId}>
                                            {tabPermissions[tabIndex] === false ? (
                                                <NoAccessMessage tabId={tabIndex} />
                                            ) : (
                                                renderTabContent(tabName, tabIndex)
                                            )}

                                            {page_type === "add" && tabPermissions[tabIndex] !== false && (
                                                <div className="mt-3 d-flex justify-content-between">
                                                    <CButton.Secondary
                                                        text={tabIndex === 1 ? "Cancel" : "Back"}
                                                        onClick={() =>
                                                            tabIndex === 1
                                                                ? props.history.push("/merchant-groups")
                                                                : navigateToPreviousTab()
                                                        }
                                                        disabled={tabIndex === 1 ? false : parseInt(activeTab) <= 1}
                                                    />
                                                    <div>
                                                        {renderNavigationButtons(tabName, tabIndex)}
                                                    </div>
                                                </div>
                                            )}
                                        </TabPane>
                                    )
                                })}
                            </TabContent>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default MerchantGroupDetail
